import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, Clock, AlertTriangle, X } from 'lucide-react';
import { getDeviceId } from '../lib/deviceId';
import { usePractice } from '../contexts/PracticeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format seconds to MM:SS
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Get escalation color based on thresholds
const getEscalationColor = (seconds, warningThreshold, criticalThreshold) => {
  if (seconds >= criticalThreshold) return 'bg-red-600 animate-pulse';
  if (seconds >= warningThreshold) return 'bg-amber-500';
  return 'bg-emerald-500';
};

// Individual custom button component
function CustomButton({ button, activeAlert, onTrigger, onClear, practiceThresholds }) {
  const [isOpen, setIsOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const menuRef = useRef(null);
  
  const warningThreshold = button.warning_threshold || practiceThresholds?.warning || 300;
  const criticalThreshold = button.critical_threshold || practiceThresholds?.critical || 420;
  
  // Timer effect
  useEffect(() => {
    if (!activeAlert || !button.has_timer) return;
    
    const createdAt = new Date(activeAlert.created_at);
    const updateElapsed = () => {
      const now = new Date();
      setElapsed(Math.floor((now - createdAt) / 1000));
    };
    
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [activeAlert, button.has_timer]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleTrigger = (message = '') => {
    onTrigger(button.id, message);
    setIsOpen(false);
    setCustomMessage('');
  };
  
  const handleClear = () => {
    onClear(button.id);
  };
  
  const isActive = !!activeAlert;
  const escalationColor = button.has_timer && isActive 
    ? getEscalationColor(elapsed, warningThreshold, criticalThreshold)
    : '';
  
  // Determine if button should have full rounded corners or split style
  const hasDropdownOrClear = button.has_dropdown || isActive;
  const buttonRounding = hasDropdownOrClear ? 'rounded-l-xl' : 'rounded-xl';
  
  return (
    <div className="relative" ref={menuRef}>
      <div className="flex">
        {/* Main button */}
        <button
          onClick={() => isActive ? handleClear() : (button.has_dropdown ? setIsOpen(!isOpen) : handleTrigger())}
          className={`
            px-5 py-2.5 ${buttonRounding} font-medium text-white transition-all
            flex items-center gap-2 min-w-[140px] justify-center
            ${isActive 
              ? `${escalationColor || 'bg-slate-600'} hover:opacity-90`
              : 'hover:opacity-90'
            }
          `}
          style={{ backgroundColor: isActive ? undefined : button.color }}
          data-testid={`custom-button-${button.id}`}
        >
          {/* Escalation marker */}
          {button.has_escalation && isActive && elapsed >= warningThreshold && (
            <AlertTriangle className="w-4 h-4 animate-bounce" />
          )}
          
          <span className="truncate max-w-[120px]">
            {isActive ? (activeAlert.metadata?.custom_message || button.name) : button.name}
          </span>
          
          {/* Timer display */}
          {button.has_timer && isActive && (
            <span className="flex items-center gap-1 text-xs bg-black/20 px-2 py-0.5 rounded-full">
              <Clock className="w-3 h-3" />
              {formatTime(elapsed)}
            </span>
          )}
        </button>
        
        {/* Dropdown button */}
        {button.has_dropdown && !isActive && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="px-3 py-2.5 rounded-r-xl text-white hover:opacity-90 border-l border-white/20"
            style={{ backgroundColor: button.color }}
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
        
        {/* Clear button when active */}
        {isActive && (
          <button
            onClick={handleClear}
            className="px-2 py-2 rounded-r-xl bg-slate-700 text-white hover:bg-slate-600 border-l border-white/20"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* Dropdown menu */}
      {isOpen && button.has_dropdown && (
        <div className="absolute bottom-full mb-2 left-0 w-64 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
          <div className="p-3 border-b border-slate-100">
            <p className="text-xs text-slate-500 mb-2">Quick options:</p>
            {button.dropdown_options?.length > 0 ? (
              <div className="space-y-1">
                {button.dropdown_options.map((option, i) => (
                  <button
                    key={i}
                    onClick={() => handleTrigger(option)}
                    className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    {option}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">No quick options configured</p>
            )}
          </div>
          
          <div className="p-3">
            <p className="text-xs text-slate-500 mb-2">Custom message:</p>
            <input
              type="text"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Enter custom message..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-dp-500"
              onKeyDown={(e) => e.key === 'Enter' && handleTrigger(customMessage)}
            />
            <button
              onClick={() => handleTrigger(customMessage)}
              className="mt-2 w-full px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: button.color }}
            >
              Send Alert
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FloatingButtonIsland() {
  const [buttons, setButtons] = useState([]);
  const [activeAlerts, setActiveAlerts] = useState({});
  const { practice } = usePractice();
  
  // Fetch custom buttons
  useEffect(() => {
    const fetchButtons = async () => {
      try {
        const response = await axios.get(`${API}/practice/custom-buttons`);
        setButtons(response.data.filter(b => b.enabled));
      } catch (error) {
        console.error('Failed to fetch custom buttons:', error);
      }
    };
    
    fetchButtons();
    const interval = setInterval(fetchButtons, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);
  
  // Fetch active alerts for custom buttons
  useEffect(() => {
    const fetchActiveAlerts = async () => {
      try {
        const response = await axios.get(`${API}/alerts?status=active`);
        const customAlerts = response.data.filter(a => a.alert_type === 'custom_button');
        const alertMap = {};
        customAlerts.forEach(alert => {
          const buttonId = alert.metadata?.button_id;
          if (buttonId) {
            alertMap[buttonId] = alert;
          }
        });
        setActiveAlerts(alertMap);
      } catch (error) {
        console.error('Failed to fetch alerts:', error);
      }
    };
    
    fetchActiveAlerts();
    const interval = setInterval(fetchActiveAlerts, 2000); // Poll every 2s
    return () => clearInterval(interval);
  }, []);
  
  const handleTrigger = async (buttonId, message = '') => {
    try {
      await axios.post(`${API}/custom-button/${buttonId}/trigger`, {
        message,
        device_id: getDeviceId()
      });
      toast.success('Alert triggered');
    } catch (error) {
      toast.error('Failed to trigger alert');
    }
  };
  
  const handleClear = async (buttonId) => {
    try {
      await axios.post(`${API}/custom-button/${buttonId}/clear`);
      toast.success('Alert cleared');
    } catch (error) {
      toast.error('Failed to clear alert');
    }
  };
  
  if (buttons.length === 0) return null;
  
  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40">
      <div className="flex items-center gap-3 glass-panel rounded-2xl px-6 py-4">
        {buttons.map((button) => (
          <CustomButton
            key={button.id}
            button={button}
            activeAlert={activeAlerts[button.id]}
            onTrigger={handleTrigger}
            onClear={handleClear}
            practiceThresholds={{
              warning: practice?.warning_threshold || 300,
              critical: practice?.critical_threshold || 420
            }}
          />
        ))}
      </div>
    </div>
  );
}
